// Game Section management service
// Each League has its own library of games with photos, rules, and average durations
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { v4 as uuidv4 } from 'uuid';

// Create a new game in the game section
export const createGameSection = async (leagueId, name, categoryId, categoryName, rulesUrl = '', photoFile = null) => {
  const gameSectionId = uuidv4();
  const gameSectionRef = doc(db, 'gameSections', gameSectionId);

  let photoUrl = null;

  // Upload photo if provided
  if (photoFile) {
    const photoRef = ref(storage, `gameSections/${leagueId}/${gameSectionId}/photo`);
    await uploadBytes(photoRef, photoFile);
    photoUrl = await getDownloadURL(photoRef);
  }

  const gameSection = {
    leagueId,
    name,
    categoryId,
    categoryName,
    rulesUrl: rulesUrl || '',
    photoUrl,
    averageDurationMinutes: null, // Will be calculated from actual game plays
    totalTimesPlayed: 0,
    totalDurationMinutes: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  await setDoc(gameSectionRef, gameSection);

  return {
    id: gameSectionId,
    ...gameSection
  };
};

// Get game section by ID
export const getGameSection = async (gameSectionId) => {
  const gameSectionRef = doc(db, 'gameSections', gameSectionId);
  const gameSectionSnap = await getDoc(gameSectionRef);

  if (!gameSectionSnap.exists()) {
    return null;
  }

  return {
    id: gameSectionSnap.id,
    ...gameSectionSnap.data()
  };
};

// Get all game sections for a league
export const getGameSectionsForLeague = async (leagueId) => {
  const gameSectionsRef = collection(db, 'gameSections');
  const q = query(gameSectionsRef, where('leagueId', '==', leagueId));
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Get game sections for a specific category in a league
export const getGameSectionsForCategory = async (leagueId, categoryId) => {
  const gameSectionsRef = collection(db, 'gameSections');
  const q = query(
    gameSectionsRef,
    where('leagueId', '==', leagueId),
    where('categoryId', '==', categoryId)
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Update game section details
export const updateGameSection = async (gameSectionId, updates) => {
  const gameSectionRef = doc(db, 'gameSections', gameSectionId);
  await updateDoc(gameSectionRef, {
    ...updates,
    updatedAt: serverTimestamp()
  });
};

// Update game section photo
export const updateGameSectionPhoto = async (gameSectionId, leagueId, photoFile) => {
  const photoRef = ref(storage, `gameSections/${leagueId}/${gameSectionId}/photo`);
  await uploadBytes(photoRef, photoFile);
  const photoUrl = await getDownloadURL(photoRef);

  await updateGameSection(gameSectionId, { photoUrl });

  return photoUrl;
};

// Delete game section photo
export const deleteGameSectionPhoto = async (gameSectionId, leagueId) => {
  try {
    const photoRef = ref(storage, `gameSections/${leagueId}/${gameSectionId}/photo`);
    await deleteObject(photoRef);
  } catch (err) {
    // Photo might not exist, ignore error
    console.log('Photo not found or already deleted');
  }

  await updateGameSection(gameSectionId, { photoUrl: null });
};

// Delete game section
export const deleteGameSection = async (gameSectionId, leagueId) => {
  // Delete photo first
  await deleteGameSectionPhoto(gameSectionId, leagueId);

  // Delete document
  const gameSectionRef = doc(db, 'gameSections', gameSectionId);
  await deleteDoc(gameSectionRef);
};

// Update average duration when a game is played
// This is called when a game timer is stopped or edited
export const updateGameSectionDuration = async (gameSectionId, durationMinutes) => {
  if (!gameSectionId || !durationMinutes || durationMinutes <= 0) return;

  const gameSection = await getGameSection(gameSectionId);
  if (!gameSection) return;

  const newTotalTimesPlayed = (gameSection.totalTimesPlayed || 0) + 1;
  const newTotalDurationMinutes = (gameSection.totalDurationMinutes || 0) + durationMinutes;
  const newAverageDuration = newTotalDurationMinutes / newTotalTimesPlayed;

  await updateGameSection(gameSectionId, {
    totalTimesPlayed: newTotalTimesPlayed,
    totalDurationMinutes: newTotalDurationMinutes,
    averageDurationMinutes: Math.round(newAverageDuration)
  });
};

// Recalculate average duration (used when editing game times)
// Pass the old duration and new duration to adjust
export const recalculateGameSectionDuration = async (gameSectionId, oldDuration, newDuration) => {
  if (!gameSectionId) return;

  const gameSection = await getGameSection(gameSectionId);
  if (!gameSection) return;

  let newTotalDurationMinutes = gameSection.totalDurationMinutes || 0;

  // Remove old duration if it was counted before
  if (oldDuration && oldDuration > 0) {
    newTotalDurationMinutes -= oldDuration;
  }

  // Add new duration
  if (newDuration && newDuration > 0) {
    newTotalDurationMinutes += newDuration;
  }

  // Ensure we don't go negative
  newTotalDurationMinutes = Math.max(0, newTotalDurationMinutes);

  const totalTimesPlayed = gameSection.totalTimesPlayed || 1;
  const newAverageDuration = totalTimesPlayed > 0 ? newTotalDurationMinutes / totalTimesPlayed : 0;

  await updateGameSection(gameSectionId, {
    totalDurationMinutes: newTotalDurationMinutes,
    averageDurationMinutes: Math.round(newAverageDuration)
  });
};

// Find game section by name and category in a league
export const findGameSectionByNameAndCategory = async (leagueId, name, categoryId) => {
  const gameSections = await getGameSectionsForLeague(leagueId);
  return gameSections.find(
    gs => gs.name.toLowerCase() === name.toLowerCase() && gs.categoryId === categoryId
  );
};

// Get or create game section (useful for migration and auto-creation)
export const getOrCreateGameSection = async (leagueId, name, categoryId, categoryName) => {
  const existing = await findGameSectionByNameAndCategory(leagueId, name, categoryId);
  if (existing) {
    return existing;
  }

  return createGameSection(leagueId, name, categoryId, categoryName);
};

// Get unique categories from a league's game sections
export const getUniqueCategoriesForLeague = async (leagueId) => {
  const gameSections = await getGameSectionsForLeague(leagueId);

  // Build unique categories map
  const categoriesMap = new Map();
  gameSections.forEach(gs => {
    if (gs.categoryName && !categoriesMap.has(gs.categoryName)) {
      categoriesMap.set(gs.categoryName, {
        name: gs.categoryName,
        categoryId: gs.categoryId
      });
    }
  });

  return Array.from(categoriesMap.values()).sort((a, b) => a.name.localeCompare(b.name));
};

// Check if a game with the same name already exists in the league (case-insensitive)
export const gameExistsInLeague = async (leagueId, gameName) => {
  const gameSections = await getGameSectionsForLeague(leagueId);
  return gameSections.some(
    gs => gs.name.toLowerCase().trim() === gameName.toLowerCase().trim()
  );
};

// Rate a game (1-6 scale)
// Stores individual ratings and updates the average
export const rateGameSection = async (gameSectionId, userId, rating) => {
  if (!gameSectionId || !userId || !rating) return;
  if (rating < 1 || rating > 6) throw new Error('Rating must be between 1 and 6');

  // Get current game section
  const gameSection = await getGameSection(gameSectionId);
  if (!gameSection) throw new Error('Game not found');

  // Get or initialize ratings object
  const ratings = gameSection.ratings || {};
  const previousRating = ratings[userId];

  // Update this user's rating
  ratings[userId] = rating;

  // Calculate new average
  const allRatings = Object.values(ratings);
  const totalRatings = allRatings.length;
  const sumRatings = allRatings.reduce((sum, r) => sum + r, 0);
  const averageRating = totalRatings > 0 ? sumRatings / totalRatings : 0;

  await updateGameSection(gameSectionId, {
    ratings,
    totalRatings,
    averageRating: Math.round(averageRating * 10) / 10 // Round to 1 decimal
  });

  return {
    previousRating,
    newRating: rating,
    averageRating: Math.round(averageRating * 10) / 10,
    totalRatings
  };
};

// Get user's rating for a game
export const getUserRating = async (gameSectionId, userId) => {
  const gameSection = await getGameSection(gameSectionId);
  if (!gameSection) return null;
  return gameSection.ratings?.[userId] || null;
};

// Mark a game as played by the user (toggle)
export const markGameAsPlayed = async (gameSectionId, userId, hasPlayed) => {
  if (!gameSectionId || !userId) return;

  const gameSection = await getGameSection(gameSectionId);
  if (!gameSection) throw new Error('Game not found');

  // Get or initialize playedBy array
  let playedBy = gameSection.playedBy || [];

  if (hasPlayed) {
    // Add user to playedBy if not already there
    if (!playedBy.includes(userId)) {
      playedBy = [...playedBy, userId];
    }
  } else {
    // Remove user from playedBy
    playedBy = playedBy.filter(id => id !== userId);
  }

  await updateGameSection(gameSectionId, {
    playedBy,
    playedByCount: playedBy.length
  });

  return {
    playedBy,
    playedByCount: playedBy.length
  };
};
